/**
 * Main page for the "Ask a Tutor" feature
 */
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TutorConversation from "../components/TutorConversation";
import TutorSidebar from "../components/TutorSidebar";
import { useTutor } from "../hooks/useTutor";

function TutorPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const {
    conversations,
    currentConversation,
    messages,
    loading,
    sendingMessage,
    sendMessage,
    deleteConversation,
  } = useTutor(conversationId);

  // Handle new chat button click - just navigate to base tutor route
  const handleNewChat = () => {
    navigate("/tutor");
  };

  return (
    <AuthenticatedLayout>
      <div className="relative flex h-[calc(100vh-4rem)]">
        {/* Sidebar with conversation history */}
        <TutorSidebar
          conversations={conversations}
          currentConversationId={conversationId}
          loading={false} /* Never show loading in sidebar */
          onNewChat={handleNewChat}
          onDeleteConversation={deleteConversation}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />

        {/* Main conversation area */}
        <div
          className={`grow flex flex-col md:translate-x-0 transition-transform duration-300 ease-in-out ${
            sidebarOpen ? "translate-x-1/3" : "translate-x-0"
          }`}
        >
          {/* Header with Tutor logo */}
          {currentConversation && (
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 md:px-5 py-4">
              <div className="flex items-center justify-between">
                {/* Mobile menu button */}
                <button
                  className="md:hidden text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <svg
                    className="w-6 h-6 fill-current"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="4" y="5" width="16" height="2" />
                    <rect x="4" y="11" width="16" height="2" />
                    <rect x="4" y="17" width="16" height="2" />
                  </svg>
                </button>

                {/* Conversation info with Tutor image */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 mr-4">
                    <img
                      src="/assets/images/avatar.png"
                      alt="Tutor"
                      className="w-12 h-12 rounded-full"
                    />
                  </div>
                  <div>
                    <h2
                      className={`text-lg font-semibold ${theme.text.primary}`}
                    >
                      Tutor
                    </h2>
                    <p className={`text-sm ${theme.text.muted}`}>
                      {currentConversation.title}
                    </p>
                  </div>
                </div>

                <div className="w-6">
                  {/* Placeholder to balance the flex layout */}
                </div>
              </div>
            </div>
          )}

          {/* Conversation content */}
          <div className="flex-1 overflow-hidden">
            {loading && !sendingMessage ? (
              <div className="h-full flex items-center justify-center">
                <div className={`text-center ${theme.text.muted}`}>
                  <div
                    className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.border.brand} mx-auto mb-4`}
                  ></div>
                  <p>Loading conversation...</p>
                </div>
              </div>
            ) : (
              <TutorConversation
                messages={messages}
                onSendMessage={sendMessage}
                loading={sendingMessage}
                conversationTitle={currentConversation?.title}
              />
            )}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}

export default TutorPage;
